import type { ReactNode } from 'react';
import { Chip, type ChipColor } from '@coordio/ui';
import {
  failModeLabels,
  guardrailActionLabels,
  guardrailSafetyTierLabels,
  lifecycleStatusLabels,
  type GuardrailDto,
} from '../../types/guardrail';

const prettyJson = (raw?: string | null): string => {
  if (!raw) return '';
  try {
    return JSON.stringify(JSON.parse(raw), null, 2);
  } catch {
    return raw;
  }
};

const DetailRow = ({ label, value }: { label: string; value: ReactNode }) => (
  <>
    <span className="text-xs text-[var(--color-text-secondary)]">{label}</span>
    <span className="text-sm">{value}</span>
  </>
);

const statusColor = (status: number): ChipColor => {
  switch (status) {
    case 1:
      return 'amber';
    case 2:
      return 'green';
    case 3:
      return 'blue';
    default:
      return 'default';
  }
};

/** Read-only guardrail version metadata and config JSON. */
export const GuardrailVersionDetail = ({ guardrail }: { guardrail: GuardrailDto }) => (
  <div className="flex flex-col gap-5">
    <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 items-baseline">
      <DetailRow label="Key" value={<span className="font-mono">{guardrail.guardrailKey}</span>} />
      <DetailRow label="Version" value={`v${guardrail.version}`} />
      <DetailRow
        label="Status"
        value={
          <Chip
            label={lifecycleStatusLabels[guardrail.status] ?? String(guardrail.status)}
            color={statusColor(guardrail.status)}
            className="text-xs"
          />
        }
      />
      <DetailRow
        label="Current"
        value={guardrail.isCurrent ? <Chip label="Current" color="violet" className="text-xs" /> : 'No'}
      />
      <DetailRow label="Name" value={guardrail.name} />
      {guardrail.description && <DetailRow label="Description" value={guardrail.description} />}
      <DetailRow label="Type" value={<Chip label={guardrail.typeKey} className="text-xs" variant="outlined" />} />
      <DetailRow label="Owner" value={guardrail.owner} />
      <DetailRow
        label="Safety tier"
        value={guardrailSafetyTierLabels[guardrail.safetyTier] ?? String(guardrail.safetyTier)}
      />
      <DetailRow label="Non-suppressible" value={guardrail.isNonSuppressible ? 'Yes' : 'No'} />
      {guardrail.minRecallTarget != null && (
        <DetailRow label="Min recall" value={String(guardrail.minRecallTarget)} />
      )}
    </div>

    <div>
      <h3 className="text-sm font-semibold mb-2">Default actions</h3>
      <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 items-baseline">
        <DetailRow
          label="Violation"
          value={guardrailActionLabels[guardrail.defaultViolationAction] ?? String(guardrail.defaultViolationAction)}
        />
        <DetailRow
          label="Inconclusive"
          value={
            guardrail.defaultInconclusiveAction != null
              ? guardrailActionLabels[guardrail.defaultInconclusiveAction] ??
                String(guardrail.defaultInconclusiveAction)
              : '—'
          }
        />
        <DetailRow
          label="Error"
          value={
            guardrail.defaultErrorAction != null
              ? guardrailActionLabels[guardrail.defaultErrorAction] ?? String(guardrail.defaultErrorAction)
              : '—'
          }
        />
        <DetailRow label="Severity" value={guardrail.defaultSeverity} />
        <DetailRow
          label="Fail mode"
          value={failModeLabels[guardrail.defaultFailMode] ?? String(guardrail.defaultFailMode)}
        />
        <DetailRow label="Timeout" value={`${guardrail.defaultTimeoutMs} ms`} />
      </div>
    </div>

    {(guardrail.approvedBy ||
      guardrail.approvedByRef ||
      guardrail.validationEvalRunId ||
      guardrail.changeReason) && (
      <div>
        <h3 className="text-sm font-semibold mb-2">Governance</h3>
        <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 items-baseline">
          {guardrail.approvedBy && <DetailRow label="Approved by" value={guardrail.approvedBy} />}
          {guardrail.approvedByRef && <DetailRow label="Approver ref" value={guardrail.approvedByRef} />}
          {guardrail.approvedAtUtc && (
            <DetailRow label="Approved at" value={new Date(guardrail.approvedAtUtc).toLocaleString()} />
          )}
          {guardrail.validationEvalRunId && (
            <DetailRow label="Eval run" value={<span className="font-mono">{guardrail.validationEvalRunId}</span>} />
          )}
          {guardrail.validationReportRef && (
            <DetailRow label="Validation report" value={guardrail.validationReportRef} />
          )}
          {guardrail.suiteRef && <DetailRow label="Suite ref" value={guardrail.suiteRef} />}
          {guardrail.changeReason && <DetailRow label="Change reason" value={guardrail.changeReason} />}
          {guardrail.effectiveFromUtc && (
            <DetailRow label="Effective from" value={new Date(guardrail.effectiveFromUtc).toLocaleString()} />
          )}
        </div>
      </div>
    )}

    <div>
      <h3 className="text-sm font-semibold mb-2">Config JSON</h3>
      <span className="text-xs text-[var(--color-text-secondary)] block mb-1">
        Config hash: <span className="font-mono">{guardrail.configHash}</span>
      </span>
      <pre className="m-0 p-3 rounded-lg bg-[var(--color-bg-inset,#fafafa)] overflow-auto max-h-[360px] font-mono text-[0.8125rem] whitespace-pre-wrap break-words">
        {prettyJson(guardrail.configJson)}
      </pre>
    </div>

    <div className="grid grid-cols-[120px_1fr] gap-x-3 gap-y-1 items-baseline">
      <DetailRow label="Created" value={new Date(guardrail.createdAt).toLocaleString()} />
      <DetailRow label="Updated" value={new Date(guardrail.updatedAt).toLocaleString()} />
      <DetailRow label="Row ID" value={<span className="font-mono">{guardrail.id}</span>} />
    </div>
  </div>
);
